import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json';
import {
  listGroupMembers,
  addGroupMember,
  setGroupMemberRoles,
  removeGroupMember,
} from '../../../services/groupMembersApi';
import { initials } from './InitialsAvatar';

/**
 * Éditeur de trombinoscope d'un GROUPE, quel que soit son type : l'onglet
 * « Paramètres du projet → Utilisateurs » (groupe `project`) et l'écran CRM
 * « Sociétés (filiales) » (groupe `company`) partagent ce composant.
 *
 * Liste les membres avec leurs rôles et laisse un gestionnaire ajouter un
 * utilisateur existant, cocher/décocher un rôle, ou retirer un membre. Tout
 * passe par /api/group/{id}/members puis re-fetch.
 *
 * Les rôles proposés viennent de l'API (`availableRoles`), déduits du type du
 * groupe — rien n'est codé ici. L'appartenance est la source de vérité ; le
 * champ `members` du groupe est une trace re-dérivée côté back
 * (orbit_group). Les affordances d'édition n'apparaissent que si l'API annonce
 * `canManage` (le back l'impose de toute façon).
 *
 * Props:
 *   groupId           number|string
 *   availableUsers    array — tous les utilisateurs [{id,name,email}] (source d'ajout)
 *   onMembersChanged  (memberIds:number[]) => void — ex. re-cadrer les assignés
 */
const GroupMembersManager = ({ groupId, availableUsers = [], onMembersChanged }) => {
  const [members, setMembers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyUid, setBusyUid] = useState(null);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  // Utilisateur en cours d'ajout. Le temps de l'aller-retour serveur puis du
  // re-fetch, il n'est NI dans la liste des membres NI dans les candidats :
  // sans ce témoin, le clic reste sans effet visible.
  const [addingUser, setAddingUser] = useState(null);
  // Uid ajoutés DEPUIS l'ouverture du panneau, le plus récent en tête. Le back
  // sert le trombinoscope trié par nom : sans cette trace, un ajout se range
  // dans l'alphabet, parfois hors écran. On les remonte en tête pour que le
  // nouveau membre s'affiche juste sous le champ d'ajout, prêt à recevoir ses
  // rôles ; la liste reprend son ordre alphabétique à la prochaine ouverture.
  const [recentUids, setRecentUids] = useState([]);

  // Keep onMembersChanged out of refetch's deps (it arrives as a fresh inline
  // arrow each parent render); route it through a ref so refetch is stable and
  // the mount effect fires once per groupId, not on every parent re-render.
  const onChangedRef = useRef(onMembersChanged);
  useEffect(() => { onChangedRef.current = onMembersChanged; });

  const refetch = useCallback(async () => {
    if (!groupId) { setLoading(false); return; }
    setError('');
    try {
      const data = await listGroupMembers(groupId);
      const list = Array.isArray(data.members) ? data.members : [];
      setMembers(list);
      if (Array.isArray(data.availableRoles) && data.availableRoles.length) setAvailableRoles(data.availableRoles);
      setCanManage(!!data.canManage);
      if (onChangedRef.current) onChangedRef.current(list.map(m => Number(m.uid)));
    } catch (e) {
      setError(e?.message || 'Échec du chargement des membres.');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { setLoading(true); setRecentUids([]); refetch(); }, [refetch]);

  const memberUids = useMemo(() => new Set(members.map(m => Number(m.uid))), [members]);
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (availableUsers || [])
      .filter(u => !memberUids.has(Number(u.id)))
      .filter(u => !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [availableUsers, memberUids, search]);

  // Ordre d'affichage : ajouts récents en tête (dernier ajouté en premier),
  // puis le reste dans l'ordre alphabétique servi par le back.
  const orderedMembers = useMemo(() => {
    if (!recentUids.length) return members;
    const rank = new Map(recentUids.map((uid, i) => [uid, i]));
    const recent = members.filter(m => rank.has(Number(m.uid)));
    const rest = members.filter(m => !rank.has(Number(m.uid)));
    recent.sort((a, b) => rank.get(Number(a.uid)) - rank.get(Number(b.uid)));
    return [...recent, ...rest];
  }, [members, recentUids]);

  const run = async (uid, fn) => {
    setBusyUid(uid); setError('');
    try { await fn(); await refetch(); }
    catch (e) { setError(e?.message || 'Opération échouée.'); }
    finally { setBusyUid(null); }
  };

  const handleAdd = async (user) => {
    if (addingUser) return;
    setSearch(''); setShowDropdown(false);
    setAddingUser(user);
    const uid = Number(user.id);
    setRecentUids(prev => [uid, ...prev.filter(u => u !== uid)]);
    try {
      // `run` enchaîne l'ajout PUIS le re-fetch : le loader ne s'éteint qu'une
      // fois le nouveau membre réellement affiché (ou l'erreur posée).
      await run(Number(user.id), () => addGroupMember(groupId, { uid: Number(user.id), roles: [] }));
    } finally {
      setAddingUser(null);
    }
  };
  const handleToggleRole = (member, roleId) => {
    const has = (member.roles || []).some(r => r.id === roleId);
    const next = has
      ? (member.roles || []).filter(r => r.id !== roleId).map(r => r.id)
      : [...(member.roles || []).map(r => r.id), roleId];
    run(member.uid, () => setGroupMemberRoles(groupId, member.uid, next));
  };
  const handleRemove = (member) => {
    setRecentUids(prev => prev.filter(u => u !== Number(member.uid)));
    return run(member.uid, () => removeGroupMember(groupId, member.uid));
  };

  const avatarFor = (u) => (u.name ? initials(u.name) : '?');

  return (
    <div className="space-y-6">
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {/* Add user (managers only) */}
      {canManage && (
        <div className="relative" aria-busy={!!addingUser}>
          <label className="field-label mb-2">Ajouter un utilisateur</label>
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher un utilisateur de la base…"
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-iris-500 focus:border-iris-500 disabled:bg-slate-50 disabled:text-slate-400"
              value={search}
              disabled={!!addingUser}
              onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            />
            {addingUser ? (
              <span className="spinner w-5 h-5 absolute left-3 top-2.5" aria-hidden="true" />
            ) : (
              <svg className="w-5 h-5 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
            {showDropdown && !addingUser && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {candidates.length > 0 ? candidates.map(user => (
                  <div
                    key={user.id}
                    className="px-4 py-3 hover:bg-iris-50 cursor-pointer flex items-center gap-3 transition-colors"
                    onMouseDown={(e) => { e.preventDefault(); handleAdd(user); }}
                  >
                    <div className="avatar">
                      {avatarFor(user)}
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{user.name}</div>
                      {user.email && <div className="text-xs text-slate-500">{user.email}</div>}
                    </div>
                  </div>
                )) : (
                  <div className="px-4 py-3 text-sm text-slate-500 italic">Aucun utilisateur trouvé</div>
                )}
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-400" aria-live="polite">
            {addingUser
              ? `Ajout de ${addingUser.name || 'l’utilisateur'} en cours…`
              : "N'importe quel utilisateur de la base peut être ajouté."}
          </p>
        </div>
      )}

      {/* Members list */}
      <div>
        <h4 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
          Membres ({members.length})
        </h4>

        {loading ? (
          <div className="text-center py-8 text-slate-400">Chargement…</div>
        ) : (members.length > 0 || addingUser) ? (
          <div className="space-y-3">
            {addingUser && (
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                <span className="spinner w-10 h-10" aria-hidden="true" />
                <div>
                  <div className="font-medium text-slate-900">{addingUser.name}</div>
                  <div className="text-xs text-slate-500">Ajout en cours…</div>
                </div>
              </div>
            )}
            {orderedMembers.map(member => {
              const busy = busyUid === member.uid;
              return (
                <div key={member.uid} className={`flex flex-col p-4 bg-slate-50 rounded-lg border border-slate-100 transition-all ${busy ? 'opacity-60 pointer-events-none' : 'hover:border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 font-bold">
                        {member.name ? member.name.charAt(0).toUpperCase() : '?'}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{member.name}</div>
                        {member.email && <div className="text-xs text-slate-500">{member.email}</div>}
                      </div>
                    </div>
                    {canManage && (
                      <button
                        onClick={() => handleRemove(member)}
                        className="text-slate-400 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-colors"
                        title="Retirer" aria-label={`Retirer ${member.name}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    )}
                  </div>

                  <div className="pl-14">
                    <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Rôles :</div>
                    <div className="flex flex-wrap gap-2">
                      {availableRoles.map(role => {
                        const active = (member.roles || []).some(r => r.id === role.id);
                        return (
                          <label
                            key={role.id}
                            className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${canManage ? 'cursor-pointer' : 'cursor-default'} ${
                              active ? 'bg-iris-100 text-iris-800 border-iris-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={active}
                              disabled={!canManage}
                              onChange={() => handleToggleRole(member, role.id)}
                            />
                            {role.label}
                          </label>
                        );
                      })}
                      {(member.roles || []).length === 0 && (
                        <span className="text-xs text-slate-400 italic self-center">Aucun rôle</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-lg border border-dashed border-slate-300">
            Aucun membre pour le moment
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Enveloppe reactive-json du composant ci-dessus : reactive-json passe les props
 * BRUTES (`~~.membersGroupId` arrive tel quel), il faut les résoudre. Sert
 * l'écran « Sociétés (filiales) », écrit en YAML :
 *
 *   - type: GroupMembersPanel
 *     groupId: ~~.membersGroupId
 *     availableUsers: ~~.users
 *
 * Rien à rendre tant qu'aucun groupe n'est visé (modale fermée) → `null`.
 */
export const GroupMembersPanel = ({ props = {} }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const resolve = (value) => {
    try {
      return evaluateTemplateValue({ valueToEvaluate: value, globalDataContext, templateContext });
    }
    catch {
      return undefined;
    }
  };

  const groupId = resolve(props.groupId);
  if (!groupId) {
    return null;
  }
  const availableUsers = resolve(props.availableUsers);

  return (
    <GroupMembersManager
      groupId={groupId}
      availableUsers={Array.isArray(availableUsers) ? availableUsers : []}
    />
  );
};

export default GroupMembersManager;
